import React, { PureComponent } from 'react'
import jsZip from 'jszip'
import { flatten } from 'lodash'
import {
  Button,
  context,
  Table,
  Link,
  MediumText,
  MutedText,
  formatDate,
  i18n,
  Checkbox as CheckboxBase,
  Tooltip,
} from '@tools/wr-catalog-base'
import { FaFilePdf, FaFilePowerpoint, FaImage, FaFileExcel, FaFileWord, FaFile } from 'react-icons/fa'
import { trackPageAction } from '@utils/tracking'
import { DownloadButtonWrapper, TableContainer } from './styles'

const Tracker = (props) => (
  <span
    onClick={() => {
      trackPageAction('click', 'additional-material', props.fileName, props.href)
    }}
  >
    {props.children}
  </span>
)

const CheckBox = ({ row }) => {
  const {
    state: { selectedDocumentsIds },
    actions: { selectDocId },
  } = context
  const attachment = row?.attachments[0]

  return (
    <Tooltip tooltip={'Select this asset'} options={{ placement: 'bottom-start' }} style={{ display: 'inline-flex' }}>
      <CheckboxBase
        checked={selectedDocumentsIds.includes(attachment.id)}
        onChange={() => selectDocId(attachment.id)}
      />
    </Tooltip>
  )
}

class DocumentsComponent extends PureComponent {
  selectAll = () => {
    const {
      state: { selectedDocumentsIds },
      actions: { selectDocId },
    } = context
    const {
      row: { control: attachments },
    } = this.props

    selectDocId(
      selectedDocumentsIds.length === attachments.map((attachment) => attachment.attachments[0].id).length
        ? []
        : attachments.map((attachment) => attachment.attachments[0].id),
    )
  }

  AssetIcon = ({ row }) => {
    const fileExtension = row.attachments[0].meta.fileName.substring(
      row.attachments[0].meta.fileName.lastIndexOf('.') + 1,
    )
    const Icon =
      {
        pdf: FaFilePdf,
        pptx: FaFilePowerpoint,
        png: FaImage,
        xlsx: FaFileExcel,
        docx: FaFileWord,
      }[fileExtension] || FaFile

    return (
      <Tracker fileName={row.attachments[0].meta.fileName} href={row.attachments[0].path}>
        <Link target="_blank" href={row.attachments[0].path} rel="noopener noreferrer">
          <Tooltip
            tooltip={`Download ${fileExtension === 'docx' ? 'Word Doc' : fileExtension.toUpperCase()}`}
            options={{ placement: 'bottom-start' }}
            style={{ display: 'inline-flex' }}
          >
            <Icon size={'1.5em'} className="documents-table" />
          </Tooltip>
        </Link>
      </Tracker>
    )
  }

  FileName = ({ row }) => {
    return (
      <Tracker fileName={row.attachments[0].meta.fileName} href={row.attachments[0].path}>
        <a target="_blank" href={row.attachments[0].path} rel="noopener noreferrer">
          {row.name}
        </a>
      </Tracker>
    )
  }

  downloadSelected = (data, selectedDocumentsIds, label, name) => {
    const jsZipInstance = new jsZip()
    const jsZipFolder = jsZipInstance.folder('Documents')

    const array = selectedDocumentsIds.map((id) => {
      const attachments = flatten(data.map((attachment) => attachment.attachments))
      const matchedAttachment = attachments.find((attachment) => attachment.id === id)

      return fetch(matchedAttachment.path)
        .then((res) => res.blob())
        .then((blob) => {
          jsZipFolder.file(matchedAttachment.meta.fileName, blob)
        })
        .catch((e) => console.log(e))
    })

    Promise.all(array).then((data) => {
      jsZipFolder.generateAsync({ type: 'blob' }).then((content) => {
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.style = 'display: none'
        const url = window.URL.createObjectURL(content)
        a.href = url
        a.download = `${label} - ${name}.zip`
        a.click()
        window.URL.revokeObjectURL(url)
        a.remove()
      })
    })
  }

  render() {
    const {
      state: { selectedDocumentsIds, selectedCatalogId },
    } = context
    const { control: data, label, description } = this.props.row
    const { name: serviceName } = this.props.product
    const isPartnerCatalog = process.env.REACT_APP_WR_PARTNER_CATALOG_ID === selectedCatalogId
    const finalData = data.filter((doc) => (isPartnerCatalog ? doc.settings?.partnerViewable === 'yes' : true))
    const helpText = data?.length && description
    const attachmentsIds = data.map((attachment) => attachment.attachments[0].id)
    const isDisabled = !Boolean(
      selectedDocumentsIds.length && selectedDocumentsIds.find((id) => attachmentsIds.includes(id)),
    )
    const columns = [
      {
        label: (
          <Tooltip
            tooltip={'Select all assets'}
            options={{ placement: 'bottom-start' }}
            style={{ display: 'inline-flex' }}
          >
            <CheckboxBase
              checked={attachmentsIds.every((id) => selectedDocumentsIds.includes(id))}
              onChange={this.selectAll}
            />
          </Tooltip>
        ),
        width: '4%',
        textAlign: 'center',
        component: CheckBox,
      },
      {
        id: 'asset',
        label: i18n('productDetails.rows.documents.asset'),
        width: '6%',
        textAlign: 'center',
        component: this.AssetIcon,
      },
      {
        id: 'fileName',
        label: i18n('productDetails.rows.documents.name'),
        component: this.FileName,
      },
      {
        id: 'description',
        label: i18n('productDetails.rows.documents.description'),
        width: '40%',
      },
      {
        id: 'settings.customerViewable',
        label: 'Customer Viewable',
        width: '12%',
        formatter: (value) => {
          const customerViewable = (value ?? '').toLowerCase() === 'yes'
          return customerViewable ? 'Yes' : 'No'
        },
      },
      {
        id: 'updatedAt',
        label: i18n('productDetails.rows.documents.lastUpdated'),
        width: '14%',
        component: ({ row }) => {
          const date1 = new Date(row.updatedAt)
          const date2 = new Date(row.attachments?.[0]?.updatedAt)

          const value = date2.getTime() > date1.getTime() ? date2 : date1

          return <span>{formatDate('MM/DD/YYYY')(value)}</span>
        },
      },
    ]

    return (
      <TableContainer>
        {label && (
          <>
            <MediumText>{label}</MediumText>
            <br />
          </>
        )}
        {helpText && <MutedText>{helpText}</MutedText>}
        <DownloadButtonWrapper>
          <Tooltip
            tooltip={isDisabled ? 'Select assets to enable download' : null}
            options={{ placement: 'bottom-start' }}
            style={{ display: 'inline-flex' }}
          >
            <Button
              onClick={(e) => {
                e.preventDefault()
                this.downloadSelected(
                  finalData,
                  selectedDocumentsIds.filter((id) => attachmentsIds.includes(id)),
                  label,
                  serviceName,
                )
              }}
              disabled={isDisabled}
            >
              {isDisabled ? 'Download' : i18n('productDetails.rows.documents.downloadSelected')}
            </Button>
          </Tooltip>
        </DownloadButtonWrapper>
        <Table
          keyField="id"
          columns={columns}
          noDataMessage={i18n('productDetails.rows.documents.noDocuments')}
          data={finalData}
        />
      </TableContainer>
    )
  }
}

export default DocumentsComponent
